// src/Pages/SuccessFollowupPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Container, Box, Typography, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, Divider, LinearProgress, CircularProgress, TextField, Chip, useTheme, useMediaQuery,} from "@mui/material";
import { useSnackbar } from "notistack";

///////////////////////////////////////////////////////////////////
//  CONSTANTS & HELPERS
///////////////////////////////////////////////////////////////////

const path = "http://192.168.1.248:3103";
const PAYMENT_STATUS = "Pending"; // only “Pending” is relevant here

///////////////////////////////////////////////////////////////////
//  MAIN COMPONENT
///////////////////////////////////////////////////////////////////

export default function SuccessFollowupPage({ loggedInUser }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { enqueueSnackbar } = useSnackbar();

    // We fetch only “Pending Payment” data:
    const [records, setRecords] = useState([]);
    const [loadingRecords, setLoadingRecords] = useState(false);

    // Search filters state (we’ll send these as params when searching).
    const [filters, setFilters] = useState({
        inquiryNumber: "",
        clientName: "",
        contactPerson: "",
        mobileNo: "",
    });

    // 1) Fetch all pending records (no filters) when “Show Pending Data” is clicked:
    const fetchAllPending = useCallback(() => {
        if (!loggedInUser) return;
        setLoadingRecords(true);

        axios
            .get(`${path}/api/payment-enquiries`, {
                params: {
                    category: "Pending Payment",
                    userId: loggedInUser.userId,
                    role: loggedInUser.role,
                    companyId: loggedInUser.companyId,
                    paymentStatus: PAYMENT_STATUS,
                    limit: 100,
                    offset: 0,
                },
            })
            .then((res) => { setRecords(res.data.enquiries || []); })
            .catch(() => { enqueueSnackbar("Failed to load pending data", { variant: "error" }); })
            .finally(() => setLoadingRecords(false));
    }, [loggedInUser, enqueueSnackbar]);

    // 2) Fetch filtered pending records when “Search” is clicked:
    const fetchFilteredPending = useCallback(() => {
        if (!loggedInUser) return;
        setLoadingRecords(true);

        // Build params: include only non-empty filter fields
        const queryParams = {
            category: "Pending Payment",
            userId: loggedInUser.userId,
            role: loggedInUser.role,
            companyId: loggedInUser.companyId,
            paymentStatus: PAYMENT_STATUS,
            limit: 100,
            offset: 0,
        };
        Object.entries(filters).forEach(([key, value]) => {
            if (value && value.trim() !== "") {
                queryParams[key] = value.trim();
            }
        });

        axios.get(`${path}/api/payment-enquiries`, { params: queryParams }).then((res) => { setRecords(res.data.enquiries || []); }).catch(() => { enqueueSnackbar("Failed to load filtered data", { variant: "error" }); }).finally(() => setLoadingRecords(false));
    }, [loggedInUser, filters, enqueueSnackbar]);

    // 3) When user clicks a table row → for now, just log it (or integrate navigation):
    const handleRowClick = (row) => {
        console.log("Row clicked:", row);
        // Previous dialog logic has been removed.
        // You could navigate to a different page here if desired, e.g.:
        // navigate(`/receivable/${row.inquiryId}`, { state: { selected: row } });
    };

    // Helper to render payment status as a chip:
    const renderStatusChip = (status) => {
        let color = "success";
        if (status === "Pending") color = "warning";
        else if (status === "Paid") color = "info";
        return <Chip label={status} color={color} size="small" variant="outlined" />;
    };

    return (
        <Container disableGutters sx={{ py: 3, pr: 3 }}>
            <Typography variant="h4" align="center" gutterBottom sx={{ fontSize: isMobile ? "1.25rem" : "2rem" }} > Payment Follow-Up </Typography>

            {/* 1) “Show Pending Data” button */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
                <Button variant="contained" onClick={fetchAllPending} disabled={loadingRecords} >
                    {loadingRecords ? ( <CircularProgress size={20} sx={{ color: "white" }} /> ) : ( "Show Pending Data" )}
                </Button>
            </Box>

            {/* 2) Search section */}
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 2, }} >
                    <TextField name="inquiryNumber" size="small" fullWidth label="Inquiry No." value={filters.inquiryNumber} onChange={(e) => setFilters((f) => ({ ...f, inquiryNumber: e.target.value })) } />
                    <TextField name="clientName" size="small" fullWidth label="Client Name" value={filters.clientName} onChange={(e) => setFilters((f) => ({ ...f, clientName: e.target.value })) } />
                    <TextField name="contactPerson" size="small" fullWidth label="Contact Person" value={filters.contactPerson} onChange={(e) => setFilters((f) => ({ ...f, contactPerson: e.target.value })) } />
                    <TextField name="mobileNo" size="small" fullWidth label="Phone No." value={filters.mobileNo} onChange={(e) => setFilters((f) => ({ ...f, mobileNo: e.target.value })) } />
                    <Button variant="contained" onClick={fetchFilteredPending}> Search </Button>
                    <Button variant="outlined" onClick={() => { setFilters({ inquiryNumber: "", clientName: "", contactPerson: "", mobileNo: "", }); fetchAllPending(); }} > Reset </Button>
                </Box>
            </Paper>

            {/* 3) Loading indicator */}
            {loadingRecords && <LinearProgress sx={{ my: 2 }} />}

            {/* 4) Table of records (only when not loading and there are records) */}
            {!loadingRecords && records.length > 0 && (
                <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {[ "#", "Inquiry No", "Date", "Client", "Contact", "Phone", "Offer", "Close", "Status", ].map((h) => (
                                    <TableCell key={h} sx={{ fontWeight: "bold", fontSize: "0.75rem" }} > {h} </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.map((r, i) => (
                                <TableRow key={r.inquiryId} hover sx={{ cursor: "pointer" }} onClick={() => handleRowClick(r)} >
                                    <TableCell sx={{ fontSize: "0.75rem" }}>{i + 1}</TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}> {r.inquiryNumber} </TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}> {r.inquiryDate ? new Date(r.inquiryDate).toLocaleDateString("en-GB") : "-"} </TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}> {r.clientname} </TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}> {r.contactPerson} </TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}> {r.mobileNo} </TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}> {r.offerPrice} </TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}> {r.closingPrice || "-"} </TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}> {renderStatusChip(r.paymentStatus)} </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* 5) If no records (after loading), show a “No records” message */}
            {!loadingRecords && records.length === 0 && (
                <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary">
                        No pending payment records found.
                    </Typography>
                </Box>
            )}
        </Container>
    );
}